import { z } from "zod";
import { dateOnlySchema, listQuerySchema, pageSchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { csvArraySchema, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";

/**
 * CONTENT CALENDAR & MEETINGS (PRD §8 Item 11, US-11.1; and the meeting half of
 * the activity timeline).
 *
 * A **content item** is one planned post: a channel, a format, a date, a
 * caption, its assets and — when it is being proofed — the deliverable it is
 * linked to. Its status stops at `scheduled` / `published`: this product does
 * not publish to social networks (PRD §5, "out of scope" table), it plans and
 * approves. A **calendar event** is an internal or client meeting, which shows
 * on the same month grid and on the client's activity timeline.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const CONTENT_CHANNELS = ["instagram", "facebook", "linkedin", "x", "youtube", "blog", "email", "other"] as const;
export type ContentChannel = (typeof CONTENT_CHANNELS)[number];
export const contentChannelSchema = z.enum(CONTENT_CHANNELS);

export const CONTENT_FORMATS = ["static", "carousel", "reel", "story", "video", "article"] as const;
export type ContentFormat = (typeof CONTENT_FORMATS)[number];
export const contentFormatSchema = z.enum(CONTENT_FORMATS);

/** US-11.1's flow, stopping where the PRD stops it. */
export const CONTENT_STATUSES = ["idea", "drafting", "in_review", "approved", "scheduled", "published"] as const;
export type ContentStatus = (typeof CONTENT_STATUSES)[number];
export const contentStatusSchema = z.enum(CONTENT_STATUSES);

export const CALENDAR_EVENT_TYPES = ["internal", "client"] as const;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];
export const calendarEventTypeSchema = z.enum(CALENDAR_EVENT_TYPES);

export const ATTENDEE_RESPONSES = ["needs_action", "accepted", "declined", "tentative"] as const;
export type AttendeeResponse = (typeof ATTENDEE_RESPONSES)[number];
export const attendeeResponseSchema = z.enum(ATTENDEE_RESPONSES);

/** "HH:MM" in the workspace timezone. Null means "some time that day". */
export const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected HH:MM");

/** A calendar month, "YYYY-MM" — the same period key the retainer quotas use. */
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Expected YYYY-MM");

/** Nothing bulk-generates more than a quarter of daily posts in one call. */
export const BULK_GENERATE_MAX = 100;

// ---------------------------------------------------------------------------
// Content items
// ---------------------------------------------------------------------------

export const contentItemSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  deliverableId: uuidSchema.nullable(),
  title: z.string(),
  channel: contentChannelSchema,
  format: contentFormatSchema,
  status: contentStatusSchema,
  scheduledFor: dateOnlySchema,
  scheduledTime: clockTimeSchema.nullable(),
  caption: z.string().nullable(),
  hashtags: z.array(z.string()),
  assetFileIds: z.array(uuidSchema),
  /** The retainer quota line this post counts against, e.g. "static post". */
  quotaDeliverableType: z.string().nullable(),
  ownerUserId: uuidSchema.nullable(),
  owner: userRefSchema.nullable(),
  publishedAt: timestampSchema.nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ContentItemDto = z.infer<typeof contentItemSchema>;

/** Shape WITHOUT defaults, so `.partial()` cannot resurrect them (zod 4). */
const contentItemWritableShape = {
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  deliverableId: uuidSchema.nullable(),
  title: z.string().trim().min(1).max(200),
  channel: contentChannelSchema,
  format: contentFormatSchema,
  status: contentStatusSchema,
  scheduledFor: dateOnlySchema,
  scheduledTime: clockTimeSchema.nullable(),
  caption: z.string().trim().max(5000).nullable(),
  hashtags: z.array(z.string().trim().min(1).max(60)).max(40),
  assetFileIds: z.array(uuidSchema).max(30),
  quotaDeliverableType: z.string().trim().max(80).nullable(),
  ownerUserId: uuidSchema.nullable(),
  notes: z.string().trim().max(2000).nullable(),
  tags: tagsSchema,
};

/** POST /api/v1/content-items (perm `calendar:write`) → 201 ContentItem. */
export const createContentItemInputSchema = z
  .object({
    ...contentItemWritableShape,
    clientId: contentItemWritableShape.clientId.optional(),
    projectId: contentItemWritableShape.projectId.optional(),
    engagementId: contentItemWritableShape.engagementId.optional(),
    deliverableId: contentItemWritableShape.deliverableId.optional(),
    format: contentItemWritableShape.format.optional(),
    status: contentItemWritableShape.status.optional(),
    scheduledTime: contentItemWritableShape.scheduledTime.optional(),
    caption: contentItemWritableShape.caption.optional(),
    hashtags: contentItemWritableShape.hashtags.optional(),
    assetFileIds: contentItemWritableShape.assetFileIds.optional(),
    quotaDeliverableType: contentItemWritableShape.quotaDeliverableType.optional(),
    ownerUserId: contentItemWritableShape.ownerUserId.optional(),
    notes: contentItemWritableShape.notes.optional(),
    tags: contentItemWritableShape.tags.optional(),
  })
  .strict();
export type CreateContentItemInput = z.input<typeof createContentItemInputSchema>;

/** PATCH /api/v1/content-items/:id (perm `calendar:write`) → ContentItem. Merge patch. */
export const updateContentItemInputSchema = z.object(contentItemWritableShape).partial().strict();
export type UpdateContentItemInput = z.input<typeof updateContentItemInputSchema>;

/** POST /api/v1/content-items/:id/reschedule (perm `calendar:write`) — the drag-and-drop seam. */
export const rescheduleContentItemInputSchema = z
  .object({
    scheduledFor: dateOnlySchema,
    scheduledTime: clockTimeSchema.nullable().optional(),
  })
  .strict();
export type RescheduleContentItemInput = z.input<typeof rescheduleContentItemInputSchema>;

/** POST /api/v1/content-items/:id/link-deliverable (perm `calendar:write`) — ties a post to its proof. */
export const linkDeliverableInputSchema = z.object({ deliverableId: uuidSchema.nullable() }).strict();
export type LinkDeliverableInput = z.input<typeof linkDeliverableInputSchema>;

/**
 * POST /api/v1/content-items/bulk-generate (perm `calendar:write`).
 * "12 static posts this month" → 12 dated placeholders, spread across the month,
 * counting what is already planned so a second call adds nothing.
 */
export const bulkGenerateContentInputSchema = z
  .object({
    engagementId: uuidSchema,
    period: monthSchema,
    /** One quota line, or every line the engagement commits to when omitted. */
    deliverableType: z.string().trim().max(80).optional(),
    channel: contentChannelSchema.optional(),
    format: contentFormatSchema.optional(),
    projectId: uuidSchema.optional(),
    ownerUserId: uuidSchema.optional(),
    titlePrefix: z.string().trim().max(80).optional(),
    /** Hard cap for this call; the quota is still the source of truth. */
    limit: z.number().int().min(1).max(BULK_GENERATE_MAX).optional(),
  })
  .strict();
export type BulkGenerateContentInput = z.input<typeof bulkGenerateContentInputSchema>;

export const bulkGenerateResultSchema = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  period: monthSchema,
  items: z.array(contentItemSchema),
});
export type BulkGenerateResultDto = z.infer<typeof bulkGenerateResultSchema>;

export const listContentItemsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  deliverableId: uuidSchema.optional(),
  ownerUserId: uuidSchema.optional(),
  channel: csvArraySchema(contentChannelSchema, CONTENT_CHANNELS.length).optional(),
  format: csvArraySchema(contentFormatSchema, CONTENT_FORMATS.length).optional(),
  status: csvArraySchema(contentStatusSchema, CONTENT_STATUSES.length).optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["scheduledFor", "createdAt", "title", "status"]).optional(),
});
export type ListContentItemsQuery = z.infer<typeof listContentItemsQuerySchema>;

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

export const calendarAttendeeSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema.nullable(),
  contactId: uuidSchema.nullable(),
  name: z.string(),
  email: z.string().nullable(),
  response: attendeeResponseSchema,
  isOrganizer: z.boolean(),
});
export type CalendarAttendeeDto = z.infer<typeof calendarAttendeeSchema>;

export const calendarEventSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  type: calendarEventTypeSchema,
  startsAt: timestampSchema,
  endsAt: timestampSchema,
  allDay: z.boolean(),
  location: z.string().nullable(),
  meetingUrl: z.string().nullable(),
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  createdByUserId: uuidSchema.nullable(),
  createdBy: userRefSchema.nullable(),
  attendees: z.array(calendarAttendeeSchema),
  cancelledAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type CalendarEventDto = z.infer<typeof calendarEventSchema>;

export const calendarAttendeeInputSchema = z
  .object({
    userId: uuidSchema.optional(),
    contactId: uuidSchema.optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    name: z.string().trim().max(120).optional(),
  })
  .strict()
  .refine((v) => Boolean(v.userId || v.contactId || v.email), { message: "Name a member, a contact or an email address." });
export type CalendarAttendeeInput = z.input<typeof calendarAttendeeInputSchema>;

const calendarEventWritableShape = {
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).nullable(),
  type: calendarEventTypeSchema,
  startsAt: timestampSchema,
  endsAt: timestampSchema,
  allDay: z.boolean(),
  location: z.string().trim().max(300).nullable(),
  meetingUrl: z.string().trim().max(500).nullable(),
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  attendees: z.array(calendarAttendeeInputSchema).max(50),
};

/** POST /api/v1/calendar-events (perm `calendar:write`) → 201 CalendarEvent, logged on the client timeline. */
export const createCalendarEventInputSchema = z
  .object({
    ...calendarEventWritableShape,
    description: calendarEventWritableShape.description.optional(),
    type: calendarEventWritableShape.type.optional(),
    allDay: calendarEventWritableShape.allDay.optional(),
    location: calendarEventWritableShape.location.optional(),
    meetingUrl: calendarEventWritableShape.meetingUrl.optional(),
    clientId: calendarEventWritableShape.clientId.optional(),
    projectId: calendarEventWritableShape.projectId.optional(),
    attendees: calendarEventWritableShape.attendees.optional(),
  })
  .strict();
export type CreateCalendarEventInput = z.input<typeof createCalendarEventInputSchema>;

/** PATCH /api/v1/calendar-events/:id (perm `calendar:write`) → CalendarEvent. `attendees` is replaced as a whole. */
export const updateCalendarEventInputSchema = z.object(calendarEventWritableShape).partial().strict();
export type UpdateCalendarEventInput = z.input<typeof updateCalendarEventInputSchema>;

/** POST /api/v1/calendar-events/:id/respond — the attendee's own RSVP. */
export const respondToEventInputSchema = z.object({ response: attendeeResponseSchema }).strict();
export type RespondToEventInput = z.input<typeof respondToEventInputSchema>;

export const listCalendarEventsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  type: calendarEventTypeSchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
  includeCancelled: queryBooleanSchema.optional(),
});
export type ListCalendarEventsQuery = z.infer<typeof listCalendarEventsQuerySchema>;

// ---------------------------------------------------------------------------
// The month / week grid
// ---------------------------------------------------------------------------

export const calendarDaySchema = z.object({
  date: dateOnlySchema,
  items: z.array(contentItemSchema),
  events: z.array(calendarEventSchema),
});
export type CalendarDayDto = z.infer<typeof calendarDaySchema>;

export const calendarViewSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  clientId: uuidSchema.nullable(),
  days: z.array(calendarDaySchema),
  totalItems: z.number().int(),
  totalEvents: z.number().int(),
  /** Per quota line: committed vs planned vs published for the window's month. */
  quotaSummary: z.array(
    z.object({
      deliverableType: z.string(),
      committedQty: z.number().int(),
      plannedQty: z.number().int(),
      publishedQty: z.number().int(),
    }),
  ),
});
export type CalendarViewDto = z.infer<typeof calendarViewSchema>;

/** GET /api/v1/content-calendar — a month ("2026-09"), or an explicit from/to window. */
export const calendarViewQuerySchema = z
  .object({
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    engagementId: uuidSchema.optional(),
    month: monthSchema.optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    channel: csvArraySchema(contentChannelSchema, CONTENT_CHANNELS.length).optional(),
    status: csvArraySchema(contentStatusSchema, CONTENT_STATUSES.length).optional(),
    ownerUserId: uuidSchema.optional(),
    includeEvents: queryBooleanSchema.optional(),
  })
  .strict();
export type CalendarViewQuery = z.infer<typeof calendarViewQuerySchema>;

// ---------------------------------------------------------------------------
// The personal iCal feed (read-only, tokenised)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/calendar/feed → the caller's own subscription URL.
 * The token is derived, not stored: rotating bumps a version, which changes the
 * signed subject, which makes every copy of the old URL stop resolving.
 */
export const calendarFeedSchema = z.object({
  url: z.string(),
  version: z.number().int(),
  revoked: z.boolean(),
  lastUsedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type CalendarFeedDto = z.infer<typeof calendarFeedSchema>;

export const calendarFeedParamsSchema = z.object({ token: z.string().min(40).max(200) });
export type CalendarFeedParams = z.infer<typeof calendarFeedParamsSchema>;

export const contentItemPageSchema = pageSchema(contentItemSchema);
export const calendarEventPageSchema = pageSchema(calendarEventSchema);
